import { useContext, useEffect, useState } from 'react';

import { useDomain, useIdentitySiteUrls } from '@netfront/gelada-identity-library';

import { CachingEntitiesContext } from 'contexts/CachingEntitiesContext';
import { PermissionContext } from 'contexts/PermissionContext';

import DashboardContext from './DashboardContext';
import { DashboardContextProps, IDashboardItemsList } from './DashboardContext.interfaces';



export function DashboardProvider({ children }: DashboardContextProps) {
  const { project, projectConfiguration } = useContext(CachingEntitiesContext);
  const { hasPermission } = useContext(PermissionContext);
  const { isDomainReady } = useDomain();
  const [dashboardItemsList, setDashboardItemsList] = useState<IDashboardItemsList[]>([]);
  const [dashboardLink, setDashboardLink] = useState<string>('');

  const { getBaseUrl: getIdentitySiteBaseUrl } = useIdentitySiteUrls({
    environment: process.env.REACT_APP_ENVIRONMENT,
    port: process.env.REACT_APP_IDENTITY_SITE_LOCAL_PORT,
  });

  useEffect(() => {
    if (!(project && isDomainReady)) {
      return;
    }

    const { id: projectId, organisation } = project;

    const hasEditPermission = hasPermission(String(projectId), 'EDIT');
    const isTranslateImprovementsAllowed = Boolean(projectConfiguration?.allowTranslateImprovements);


    const kanziBaseUrl = `/dashboard/${String(organisation.key)}/${String(projectId)}`;
    const identityBaseUrl = getIdentitySiteBaseUrl();

    const cardItems: IDashboardItemsList[] = [
      {
        id: 'style',
        description: 'Project reporting page',
        href: `${kanziBaseUrl}/style-and-layout`,
        image: '',
        isVisible: hasEditPermission,
        iconId: 'id_style_tab_icon',
        title: 'Style and layout',
        isInternal: true,
      },
      {
        id: 'integrations',
        description: 'Project reporting page',
        href: `${kanziBaseUrl}/integrations`,
        isVisible: hasEditPermission,
        image: '',
        iconId: 'id_units_tab_icon',
        title: 'Install codes',
        isInternal: true,
      },
      {
        id: 'reporting',
        description: 'Project reporting page',
        href: `${kanziBaseUrl}/reporting`,
        image: '',
        iconId: 'id_units_tab_icon',
        title: 'Reporting',
        isInternal: true,
      },
      {
        id: 'translate-improvements',
        description: 'Translate improvements page',
        href: `${kanziBaseUrl}/translate-improvements`,
        image: '',
        isVisible: isTranslateImprovementsAllowed,
        iconId: 'id_glossary_icon',
        title: 'Translate Improvements',
        isInternal: true,
      },
      {
        id: 'insights',
        description: 'Project insights page',
        href: `${identityBaseUrl}/project/${String(projectId)}/insights`,
        image: '',
        iconId: 'id_insights_icon',
        title: 'Insights',
        isInternal: false,
      },
      {
        id: 'support',
        description: 'Support page',
        href: `/contact`,
        image: '',
        isVisible: hasEditPermission,
        iconId: 'id_support_icon',
        title: 'Support',
      },
      {
        id: 'resources',
        description: 'Resources page',
        href: `/docs`,
        image: '',
        isVisible: hasEditPermission,
        iconId: 'id_resources_icon',
        title: 'Resources',
      },
      {
        id: 'team',
        description: 'team page',
        href: `${identityBaseUrl}/project/${String(projectId)}/user`,
        image: '',
        isVisible: hasEditPermission,
        iconId: 'id_users_icon',
        title: 'Team',
      },


    ];

    setDashboardItemsList(cardItems.filter(card => {
      card.subItems = card.subItems?.filter(({isVisible = true}) => isVisible) ?? [];
      return card.isVisible ?? true;
    }));
    setDashboardLink(kanziBaseUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, isDomainReady, projectConfiguration]);


  return (
    <DashboardContext.Provider
      value={{
        dashboardItems: dashboardItemsList,
        dashboardLink,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}
